import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SocialIcon } from 'react-social-icons';
import Chord from '@tombatossals/react-chords/lib/Chord';
import Accordion from './Accordion';
import TunerButton from './TunerButton';
import './styles.css';

// Define the chords as constants with explicit types
const E_CHORD = {
  frets: [0, 2, 2, 1, 0, 0] as number[],
  fingers: [0, 2, 3, 1, 0, 0] as number[],
  barres: [] as number[],
  capo: false as boolean,
};

const G_CHORD = {
  frets: [3, 2, 0, 0, 0, 1] as number[],
  fingers: [3, 2, 0, 0, 0, 1] as number[],
  barres: [] as number[],
  capo: false as boolean,
};

const C_CHORD = {
  frets: [0, 3, 2, 0, 1, 0] as number[],
  fingers: [0, 3, 2, 0, 1, 0] as number[],
  barres: [] as number[],
  capo: false as boolean,
};

const F_CHORD = {
  frets: [1, 3, 3, 2, 1, 1] as number[],
  fingers: [1, 3, 4, 2, 1, 1] as number[],
  barres: [1] as number[],
  capo: false as boolean,
};

const D_CHORD = {
  frets: [0, 0, 0, 2, 3, 1] as number[],
  fingers: [0, 0, 0, 2, 3, 1] as number[],
  barres: [] as number[],
  capo: false as boolean,
};

// Fixed the return type issue by explicitly returning JSX.Element
const MyChord = ({ chordArr }: { chordArr: typeof E_CHORD }): JSX.Element => {
  const instrument = {
    strings: 6,
    fretsOnChord: 4,
    name: 'Guitar',
    keys: [],
    tunings: {
      standard: ['E', 'A', 'D', 'G', 'B', 'E'],
    },
  };

  try {
    return <Chord chord={chordArr} instrument={instrument} lite={false} />;
  } catch (e) {
    return <Chord chord={E_CHORD} instrument={instrument} lite={false} />;
  }
};

function calculateData(number: number): typeof E_CHORD {
  const chords = [E_CHORD, G_CHORD, C_CHORD, F_CHORD, D_CHORD];
  return chords[number] || D_CHORD;
}

function App() {
  const [chordState, setChordState] = useState<typeof E_CHORD>(E_CHORD);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setChordState(calculateData(Math.floor(Math.random() * 5)));
    }, 1500);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearInterval(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div
          className="logo"
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          style={{ cursor: 'pointer' }}
        >
          JW
        </div>
        <div className="nav-links">
          <a
            href="#about"
            className="nav-link"
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('about');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            About
          </a>
          <a
            href="#lessons"
            className="nav-link"
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('lessons');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            Lessons
          </a>
          <a
            href="#pricing"
            className="nav-link"
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('pricing');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            Pricing
          </a>
          <a
            href="https://0kgjda4nd6j.typeform.com/to/xAozLYBb"
            className="nav-link nav-cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book Free Lesson
          </a>
        </div>
      </nav>

      <main className="hero">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="chord-diagram">
            <MyChord chordArr={chordState} />
          </div>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center"
          >
            John Williams
          </motion.h1>

          <motion.p
            className="subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Private Guitar & Ukulele Teacher in Carmel Valley
          </motion.p>

          <div className="cta-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="cta-button secondary-cta"
            >
              <i className="fas fa-info-circle"></i>
              Learn More
            </button>

            <a
              href="https://0kgjda4nd6j.typeform.com/to/xAozLYBb"
              className="cta-button primary-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-calendar-alt"></i>
              Book your <span className="font-bold">FREE</span> Lesson
            </a>

            <TunerButton
              href="https://johnjaguar.github.io/Tuner/"
              onClick={() => {
                console.log('Tuner button clicked');
              }}
            />
          </div>

          <div className="social-links">
            <a
              href="mailto:jaydubthedub@gmail.com"
              className="social-link email"
              aria-label="Send email"
            >
              <i className="fas fa-envelope"></i>
            </a>
            <SocialIcon
              url="https://www.linkedin.com/in/john-williams-672301235/"
              target="_blank"
            />
            <SocialIcon
              url="https://www.youtube.com/watch?v=wiqtWGMaywQ"
              network="youtube"
              target="_blank"
            />
            {/* <SocialIcon
              url="https://www.instagram.com/guitarlessons.fun"
              target="_blank"
            /> */}
            <SocialIcon
              url="https://www.yelp.com/biz/john-williams-san-diego"
              target="_blank"
            />
          </div>
        </motion.div>

        {/* Custom Modal instead of react-modal */}
        {isModalOpen && (
          <div
            className="modal-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsModalOpen(false);
              }
            }}
          >
            <div
              className="modal-content-wrapper"
              style={{
                background: 'white',
                borderRadius: '12px',
                maxWidth: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                outline: 'none',
                boxShadow:
                  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                position: 'relative',
              }}
            >
              <div
                className="modal-header"
                style={{
                  position: 'relative',
                  padding: '24px 24px 16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: '12px 12px 0 0',
                }}
              >
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{ margin: 0, color: 'white' }}
                >
                  About Guitar Lessons
                </h2>
                <button
                  onClick={() => {
                    console.log('Modal close button clicked');
                    setIsModalOpen(false);
                  }}
                  className="modal-close-button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    minWidth: '40px',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    touchAction: 'manipulation',
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ padding: '24px', background: 'white' }}>
                <Accordion />
              </div>
            </div>
          </div>
        )}

        <div className="scroll-indicator">
          <i className="fas fa-chevron-down"></i>
        </div>
      </main>

      {/* Features Section */}
      <section id="lessons" className="features-section">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-title"
          >
            Why Choose In-Home Lessons?
          </motion.h2>
          <div className="features-grid">
            {[
              {
                icon: '🏠',
                title: 'Convenient In-Home Service',
                description:
                  'I travel to your home in Carmel Valley, eliminating commute time and providing a comfortable, familiar learning environment for students.',
              },
              {
                icon: '🎸',
                title: 'All Styles & Genres',
                description:
                  'Rock, pop, classical, jazz, folk, and more. With eclectic taste and diverse skills, you can learn any style your heart desires.',
              },
              {
                icon: '👨‍👩‍👧‍👦',
                title: 'Ages 5 & Up',
                description:
                  'Teaching children, teens, and adults of all skill levels – from brand new beginners to advanced players seeking to hone their craft.',
              },
              {
                icon: '🎓',
                title: 'Expert Instruction',
                description:
                  'Musicians Institute graduate with 30+ years experience. Former Gibson employee and Vice President of Tronical USA.',
              },
              {
                icon: '✨',
                title: 'Personalized Approach',
                description:
                  'Every student is unique. I customize each lesson to match your learning style, pace, and musical interests.',
              },
              {
                icon: '🎵',
                title: 'Both Instruments',
                description:
                  'Learn guitar, ukulele, or both! Perfect for exploring different sounds and expanding your musical abilities.',
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-title"
          >
            Lesson Pricing
          </motion.h2>
          <div className="pricing-grid">
            <motion.div
              className="pricing-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3>Guitar Lessons</h3>
              <div className="price-item">
                <span className="price-label">30 minutes</span>
                <span className="price-value">$50</span>
              </div>
              <div className="price-item">
                <span className="price-label">60 minutes</span>
                <span className="price-value">$100</span>
              </div>
              <a
                href="https://0kgjda4nd6j.typeform.com/to/xAozLYBb"
                className="free-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                First Lesson FREE!
              </a>
            </motion.div>
            <motion.div
              className="pricing-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Ukulele Lessons</h3>
              <div className="price-item">
                <span className="price-label">30 minutes</span>
                <span className="price-value">$40</span>
              </div>
              <div className="price-item">
                <span className="price-label">60 minutes</span>
                <span className="price-value">$80</span>
              </div>
              <a
                href="https://0kgjda4nd6j.typeform.com/to/xAozLYBb"
                className="free-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                First Lesson FREE!
              </a>
            </motion.div>
          </div>
          <motion.div
            className="pricing-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <a
              href="https://0kgjda4nd6j.typeform.com/to/xAozLYBb"
              className="cta-button primary-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-calendar-alt"></i>
              Book your FREE Lesson Today
            </a>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-title"
          >
            About John Williams
          </motion.h2>
          <motion.div
            className="about-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p>
              For over three decades, I&apos;ve dedicated myself to the art of
              guitar playing and teaching. As a graduate of the prestigious
              Musicians Institute (MI), I&apos;ve built a comprehensive
              understanding of music theory, technique, and performance across
              multiple genres.
            </p>
            <p>
              My professional journey has kept me deeply connected to the music
              industry, including working at Gibson and serving as Vice
              President of Tronical USA. This industry experience gives me
              unique insights that I bring to every lesson.
            </p>
            <p>
              My teaching philosophy is simple: music should be fun, accessible,
              and tailored to each student&apos;s interests. Whether you dream
              of playing classical compositions, rocking out to your favorite
              songs, or exploring jazz improvisation, I&apos;ll guide you on
              your musical journey.
            </p>
          </motion.div>
          <motion.div
            className="credentials"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <span className="credential-badge">
              Musicians Institute Graduate
            </span>
            <span className="credential-badge">30+ Years Experience</span>
            <span className="credential-badge">Former Gibson Employee</span>
            <span className="credential-badge">VP Tronical USA</span>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="site-footer">
        <div className="container">
          <h3 className="footer-title">Ready to Start Your Musical Journey?</h3>
          <div className="footer-social">
            <a
              href="https://www.linkedin.com/in/john-williams-672301235/"
              className="footer-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <i className="fab fa-linkedin"></i>
            </a>
            <a
              href="https://www.youtube.com/watch?v=wiqtWGMaywQ"
              className="footer-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
            >
              <i className="fab fa-youtube"></i>
            </a>
            <a
              href="https://www.yelp.com/biz/john-williams-san-diego"
              className="footer-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Yelp"
            >
              <i className="fab fa-yelp"></i>
            </a>
          </div>
          <div className="footer-cta">
            <a
              href="https://0kgjda4nd6j.typeform.com/to/xAozLYBb"
              className="footer-cta-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book Your Free Lesson
            </a>
          </div>
          <div className="footer-info">
            <p>
              &copy; 2024 John Williams Guitar Lessons | Serving Carmel Valley &
              San Diego County
            </p>
            <p>
              Guitar & Ukulele Instruction • In-Home Lessons • All Ages & Levels
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
